// Display order for the MLI service list, which AMLI supplies as a fixed
// sequence of specialities:
//
//   Instant GP consultation, General Physician, Counselling Psychologist,
//   Consultant Physiotherapist, Endocrinologist, Diabetologist, Gynaecologist,
//   Cardiologist, Dentist, Dermatologist, Gastroenterologist, Pulmonologist,
//   Urologist, ENT Specialist, Psychiatrist, Oncologist, Ophthalmologist
//
// The sequence lives in REACT_APP_SERVICE_ORDER as comma-separated serviceIds
// rather than in code, because ids are per-environment — uat and prod are
// separate databases (cf. REACT_APP_HRA_ID, 31 on uat vs 18 on prod). Each
// .env file therefore carries its own list, with the speciality names repeated
// in a comment so the id list can be read positionally.

// Empty entries are dropped before the Number() conversion: "".split(",") is
// [""], and Number("") is 0, which would otherwise rank a phantom serviceId 0
// first and make an unset env look like a configured one.
const RANK = new Map(
  (process.env.REACT_APP_SERVICE_ORDER || "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map((part) => Number(part))
    .filter(Number.isFinite)
    .map((serviceId, index) => [serviceId, index]),
);

const UNRANKED = Number.MAX_SAFE_INTEGER;

// serviceId arrives off a JSON response; Number() on both sides keeps the
// lookup working whether it is sent as a number or a string.
function rankOf(service) {
  const rank = RANK.get(Number(service?.serviceId));
  return rank === undefined ? UNRANKED : rank;
}

// Returns a new array ordered by AMLI's sequence. Services missing from the
// list sort to the end and keep their API order relative to each other (sort is
// stable), so an incomplete staging list — or a service AMLI adds before the
// env is updated — degrades to "shown last" instead of disappearing.
//
// Never sorts in place: redux state is frozen in development.
export function sortServicesByPriority(services) {
  if (!Array.isArray(services)) return [];

  const sorted = [...services].sort((a, b) => rankOf(a) - rankOf(b));

  // The failure mode here is silent — a mistyped or missing id just sinks to
  // the bottom — so say something while developing.
  if (process.env.NODE_ENV !== "production" && RANK.size > 0) {
    const unranked = sorted
      .filter((service) => rankOf(service) === UNRANKED)
      .map((service) => `${service?.name || "unnamed"} (${service?.serviceId})`);

    if (unranked.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        "[serviceOrder] missing from REACT_APP_SERVICE_ORDER, shown last: " +
          unranked.join(", "),
      );
    }
  }

  return sorted;
}
